import { FC, useCallback, useEffect, useMemo, useState } from "react";

import { useRouter } from "next/router";

import { RcFile } from "antd/es/upload";

import { InboxOutlined } from "@ant-design/icons";
import { t, Trans } from "@lingui/macro";
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Radio,
  Space,
  TimePicker,
  Typography,
  Upload,
} from "antd";
import axios from "axios";
import { isEmpty, keyBy, orderBy } from "lodash";
import styled from "styled-components";

import FormLabel from "components/forms-view/form-label";
import ApiSelect from "components/forms/multi-select";
import HoursInput from "components/uikit/hours-input";

import { useApplicationContext } from "contexts/app";

import { FileUpload, FormQuestion } from "types";

import { parseAnswers } from "utils/navirec-forms";
import { extractUuidFromURL, sleep } from "utils/uuid-utils";

const StyledForm = styled(Form)`
  .ant-form-item-label {
    margin-bottom: 10px;
  }
`;

const StyledCheckboxGroup = styled(Checkbox.Group)`
  label {
    padding-bottom: 10px;
    width: 100%;
  }

  .ant-checkbox-inner {
    width: 20px;
    height: 20px;
  }
`;

const StyledRadioGroup = styled(Radio.Group)`
  label {
    padding-bottom: 10px;
    width: 100%;
  }

  .ant-checkbox-inner {
    width: 20px;
    height: 20px;
  }
`;

const UserInputForm: FC<{
  form: { name: string; description: string; url: string };
  questions: FormQuestion[];
  type?: "preview" | "new_response" | "view_response";
  answers?: any;
  account?: string;
}> = ({ form, questions, type, answers, account }) => {
  const { httpClient, configuration } = useApplicationContext();
  const router = useRouter();

  const questionsBySlug = useMemo(() => keyBy(questions, "slug"), [questions]);

  const [formRef] = Form.useForm();
  const [submitting, setSubmittimg] = useState<boolean>(false);
  const [fileUploadMapping, setFileUploadMapping] = useState<{ [key: string]: string }>({});
  const [uploadingFiles, setUploadingFiles] = useState<boolean>(false);

  const engineTimeSlug = questions.find((x) => x.question_type === "vehicle_total_engine_time");
  const totalEngineTime = Form.useWatch(`${engineTimeSlug?.slug as any}`, formRef);

  const orderedQuestions = useMemo(() => {
    return orderBy(questions, ["sort_order", "id"]);
  }, [questions]);

  useEffect(() => {
    if (answers && type === "view_response") {
      formRef.setFieldsValue(answers);
    }
  }, [answers, type]);

  const onSubmit = useCallback(
    async (values: any) => {
      setSubmittimg(true);

      try {
        const { attachmentAnswers, postableAnswers, vehicle, driver } = parseAnswers(
          values,
          questionsBySlug,
          fileUploadMapping
        );

        const response = await httpClient.post("form_responses/", {
          data: {
            form: form.url,
            answers: postableAnswers,
            ...(vehicle && { vehicle }),
            ...(driver && { driver }),
          },
        });

        if (response) {
          if (!isEmpty(attachmentAnswers)) {
            await Promise.all(
              attachmentAnswers.map(
                async ({ fileUpload, question }) =>
                  await httpClient.post("documents/", {
                    data: {
                      account: configuration?.account?.url,
                      file_upload: fileUpload,
                      form_question: question,
                      form_response: response.url,
                    },
                  })
              )
            );
          }

          await httpClient.post(`form_responses/${response.id}/submit/`);
          message.destroy();
          message.success(t`Form is submitted!`);

          await sleep(1000);
          router.push(
            {
              pathname: router.pathname,
              query: { success: "true" },
            },
            `/forms/${router.query.id}/responses/${router.query.status}/?success=true`,
            { shallow: false }
          );
          await sleep(1000);
          window.close();
        }
      } catch (error) {}

      setSubmittimg(false);
    },
    [questionsBySlug, fileUploadMapping]
  );

  const normFile = (e: any) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  const uploadFile = useCallback(async ({ onSuccess, file, onError }: any) => {
    setUploadingFiles(true);
    try {
      const fileUpload: FileUpload = await httpClient.post("file_uploads/", {
        data: {
          filename: (file as RcFile).name,
          mimetype: (file as RcFile).type,
        },
      });

      if (fileUpload.s3_presigned_post) {
        const { url, fields } = fileUpload.s3_presigned_post as any;

        const res = await axios.post(url, { ...fields, file }, { headers: { "Content-Type": "multipart/form-data" } });

        if (res.status !== 204) {
          throw new Error(t`Failed to post content into s3`);
        }

        setFileUploadMapping((curr) => ({ ...curr, [file.uid]: fileUpload.url }));

        onSuccess && onSuccess("ok");
      } else {
        throw new Error(t`Fileupload missing s3 presigned post url`);
      }
    } catch (error) {
      message.error(t`Uploading failed`);
      onError && onError(error as any);
    }
    setUploadingFiles(false);
  }, []);

  const renderInput = useCallback(
    (question: FormQuestion) => {
      const defaultProps = {
        key: question.id,
        name: question.slug || question.id,
        required: question.required,
        ...(question.required && {
          rules: [
            {
              required: true,
              message: t`This field is required`,
            },
          ],
        }),
      };

      switch (question.question_type) {
        case "attachment":
          return (
            <Form.Item valuePropName="fileList" getValueFromEvent={normFile} {...defaultProps}>
              <Upload.Dragger
                disabled={type !== "new_response"}
                customRequest={uploadFile}
                multiple
                name="file"
                type="drag"
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">{t`Click or drag file to this area to upload`}</p>
                <p className="ant-upload-hint">
                  {t`Support for a single or bulk upload. Strictly prohibited from uploading company data or other banned
                  files.`}
                </p>
              </Upload.Dragger>
            </Form.Item>
          );
        case "text":
          return (
            <Form.Item {...defaultProps}>
              <Input style={{ width: "100%" }} />
            </Form.Item>
          );
        case "number":
          return (
            <Form.Item {...defaultProps}>
              <InputNumber style={{ width: "100%" }} inputMode="numeric" />
            </Form.Item>
          );
        case "date":
          return (
            <Form.Item {...defaultProps}>
              <DatePicker inputReadOnly style={{ width: "100%" }} />
            </Form.Item>
          );
        case "time":
          return (
            <Form.Item {...defaultProps}>
              <TimePicker inputReadOnly showSecond={false} style={{ width: "100%" }} />
            </Form.Item>
          );
        case "single_choice":
          return (
            <Form.Item {...defaultProps}>
              <StyledRadioGroup options={question.choices?.map((x) => ({ label: x, value: x }))} />
            </Form.Item>
          );
        case "multiple_choice":
          return (
            <Form.Item {...defaultProps}>
              <StyledCheckboxGroup options={question.choices?.map((x) => ({ label: x, value: x }))} />
            </Form.Item>
          );
        case "boolean":
          return (
            <Form.Item {...defaultProps}>
              <StyledRadioGroup
                size="large"
                options={[
                  { label: t`Yes`, value: true },
                  { label: t`No`, value: false },
                ]}
              />
            </Form.Item>
          );
        case "vehicle":
          return (
            <ApiSelect
              formItemProps={defaultProps}
              selectedValues={[]}
              selectProps={{ mode: undefined }}
              labelPath="name_display"
              placeholder={t`Search for vehicles`}
              url={`/vehicles/?account=${extractUuidFromURL(account)}&active=true`}
              valuePath="url"
            />
          );
        case "driver":
          return (
            <ApiSelect
              formItemProps={defaultProps}
              selectedValues={[]}
              selectProps={{ mode: undefined }}
              labelPath="name"
              placeholder={t`Search for drivers`}
              url={`/drivers/?account=${extractUuidFromURL(account)}&active=true`}
              valuePath="url"
            />
          );
        case "vehicle_odometer":
          return (
            <Form.Item {...defaultProps}>
              <Input inputMode="numeric" addonAfter={<div>{t`km`}</div>} />
            </Form.Item>
          );
        case "vehicle_total_engine_time":
          return (
            <Form.Item {...defaultProps}>
              <HoursInput
                value={totalEngineTime}
                onChange={(val) => formRef.setFieldValue(question.slug as any, val)}
              />
            </Form.Item>
          );
        default:
          return (
            <Typography.Text type="warning">
              <Trans>Not supported question type:</Trans> {question.question_type}
            </Typography.Text>
          );
      }
    },
    [uploadFile, type, totalEngineTime]
  );

  const getAdditionalFormValuees = useCallback(async (key: "vehicle", value: string) => {
    switch (key) {
      case "vehicle": {
        try {
          const values = await httpClient.post(`/forms/${router.query.id}/initial_values/`, {
            data: {
              vehicle: value,
              answers: {},
            },
          });

          formRef.setFieldsValue({ ...formRef.getFieldsValue(), ...values.answers });
        } catch (error) {
          console.error(error);
        }

        break;
      }

      default:
        break;
    }
  }, []);

  return (
    <div
      style={{
        background: "#F5F5F5",
        display: "flex",
        justifyItems: "center",
        alignItems: "center",
        flexDirection: "column",
        maxWidth: 644,
        width: "100%",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <Typography.Paragraph strong>{form.description}</Typography.Paragraph>
        </div>
      </div>
      <StyledForm
        onValuesChange={(changedValues: { [key: string]: any }) => {
          if (changedValues.vehicle) {
            getAdditionalFormValuees("vehicle", changedValues["vehicle"]);
          }
        }}
        form={formRef}
        onFinish={onSubmit}
        onFinishFailed={(e) => {
          const { errorFields } = e;
          const fields = errorFields.map((x) => x.name?.[0]);
          message.error(t`Missing required fields: ${fields.join(", ")}`);
        }}
        scrollToFirstError
        style={{ width: "100%" }}
      >
        {orderedQuestions.map((x) => (
          <div key={x.id} style={{ background: "#FFF", marginBottom: 16, padding: 16, paddingBottom: 0 }}>
            <Space direction={"vertical"} style={{ width: "100%" }}>
              <FormLabel label={x.question} description={x.description} required={x.required} />
              {renderInput(x)}
            </Space>
          </div>
        ))}
      </StyledForm>
      <Col style={{ width: "100%", marginTop: 20 }}>
        {type === "new_response" ? (
          <Button
            disabled={submitting || uploadingFiles}
            loading={submitting}
            onClick={() => formRef.submit()}
            size="large"
            type="primary"
            style={{ width: "100%" }}
          >
            <Trans>Submit</Trans>
          </Button>
        ) : null}
      </Col>
    </div>
  );
};

export default UserInputForm;
